import { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  createChart,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle } from '@pipyar/core';
import { chartPalette } from './chartTheme';
import { showLatest } from './chartShift';
import { KnotPrimitive, type KnotLine } from './knotPrimitive';
import { cn } from '@/lib/utils';
import { useLiveBar } from '@/lib/useLiveBar';

interface KnotChartProps {
  candles: Candle[];
  knots: KnotLine[];
  showBands: boolean;
  digits: number;
  theme: 'dark' | 'light';
  resetKey?: string;
  className?: string;
  /**
   * Bars visible on open.
   *
   * Tighter than the full series on purpose: knots are body midpoints, so the
   * bodies have to be readable for the level to mean anything on screen.
   */
  visibleBars?: number;
}

/** Stored timestamps are broker wall-clock, so they must be read as UTC. */
function formatStoredTime(t: UTCTimestamp): string {
  const d = new Date((t as number) * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

export function KnotChart({
  candles,
  knots,
  showBands,
  digits,
  theme,
  resetKey,
  className,
  visibleBars = 180,
}: KnotChartProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // Last candle follows the pushed price between refetches.
  useLiveBar(seriesRef, candles);
  const primitiveRef = useRef<KnotPrimitive | null>(null);
  const positionedForRef = useRef<string | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const palette = chartPalette(theme);
    const chart = createChart(host, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: palette.text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6,
      },
      crosshair: {
        /*
         * Normal, not the library's default of Magnet.
         *
         * Magnet snaps the price readout to the nearest OHLC of the bar under
         * the pointer, so the crosshair jumps to an open or close instead of
         * following the mouse. That is useful for reading a candle and actively
         * wrong for measuring anything between them — judging where a knot sits
         * inside a body, or how far price is from a gap edge.
         */
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
        horzLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
      },
      localization: {
        timeFormatter: (t: Time) => formatStoredTime(t as UTCTimestamp),
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: palette.bull,
      downColor: palette.bear,
      borderUpColor: palette.bull,
      borderDownColor: palette.bear,
      wickUpColor: palette.bull,
      wickDownColor: palette.bear,
      priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
    });

    const primitive = new KnotPrimitive();
    primitive.setColors({
      latest: palette.knotLatest,
      lightness: palette.knotLightness,
      pending: palette.knotPending,
    });
    // Attached to the SERIES, not the pane: a knot is a price level, so the
    // renderer needs priceToCoordinate — which only the series provides.
    series.attachPrimitive(primitive);

    chartRef.current = chart;
    seriesRef.current = series;
    primitiveRef.current = primitive;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      primitiveRef.current = null;
      positionedForRef.current = null;
    };
  }, [theme, digits]);

  useEffect(() => {
    seriesRef.current?.setData(
      candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );
  }, [candles]);

  useEffect(() => {
    primitiveRef.current?.setKnots(knots);
  }, [knots]);

  useEffect(() => {
    primitiveRef.current?.setShowBands(showBands);
  }, [showBands]);

  // Position once per series, on its first data — re-centring on every refetch
  // would yank the view out from under anyone who had scrolled back.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || candles.length === 0) return;

    const key = resetKey ?? '';
    if (positionedForRef.current === key) return;
    positionedForRef.current = key;

    showLatest(chart.timeScale(), candles.length - 1, visibleBars);
  }, [resetKey, candles, visibleBars]);

  return <div ref={hostRef} className={cn('relative', className)} />;
}
